<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">
              {{ $t("TAXES.TAX_GROUPS_LIST") }}
            </h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button btn-black add"
              icon
              size="sm"
              @click="openTaxList"
              v-if="$currentUserCan($permissions.PERM_VIEW_TAXES)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-list"></i>
              </span>
              <span class="btn-inner--text">{{ $t("TAXES.TAXES") }}</span>
            </base-button>

            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openTaxGroupCreateModal"
              v-if="$currentUserCan($permissions.PERM_CREATE_TAXES)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">
                {{ $t("TAXES.ADD_TAX_GROUP") }}
              </span>
            </base-button>

            <notification-subscription
              v-if="$currentUserCan($permissions.PERM_VIEW_TAXES)"
              :objectType="'taxGroups'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <tax-group-list-table
        @onViewTaxGroup="openTaxGroupViewModal"
        @onEditTaxGroup="openTaxGroupEditModal"
        @onDeleteTaxGroup="deleteTaxGroup"
        :key="renderKey * 100"
      />

      <div
        v-if="isViewTaxGroupModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isViewTaxGroupModalOpened ? 'show' : 'hide']"
        :key="renderKey * 200"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'TAX_GROUP_VIEW'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("TAXES.VIEW_TAX_GROUP") }}
              </h1>
            </div>

            <div class="resizable-wrapper-content-header-right">
              <notification-subscription
                v-if="openTaxGroup"
                :objectType="'taxGroups'"
                :objectId="openTaxGroup.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />

              <button class="close" @click="closeTaxGroupModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>

          <div class="resizable-wrapper-content-body">
            <view-tax-group-component
              v-if="openTaxGroup"
              :taxGroupId="openTaxGroup.id"
              @onEditTaxGroup="openTaxGroupEditModal"
              @onDeleteTaxGroup="deleteTaxGroup"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isEditTaxGroupModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isEditTaxGroupModalOpened ? 'show' : 'hide']"
        :key="renderKey * 300"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'TAX_GROUP'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("TAXES.EDIT_TAX_GROUP") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button
                  class="view"
                  @click="openTaxGroupViewModal(openTaxGroup)"
                >
                  <i class="fal fa-expand-alt"></i>
                  <span>{{ $t("COMMON.VIEW") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeTaxGroupModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <edit-tax-group-component
              v-if="openTaxGroup"
              :taxGroupId="openTaxGroup.id"
              @onViewTaxGroup="openTaxGroupViewModal"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isAddTaxGroupModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isAddTaxGroupModalOpened ? 'show' : 'hide']"
        :key="renderKey * 400"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'TAX_GROUP'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("TAXES.ADD_TAX_GROUP") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeTaxGroupModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <add-tax-group-component
              @onViewTaxGroup="openTaxGroupViewModal"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from "sweetalert2";
import { Button } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import {
  QUERY_ACTIONS_VIEW,
  QUERY_ACTIONS_EDIT,
  QUERY_ACTIONS_ADD,
} from "@/constants/common";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import TaxGroupListTable from "./partials/TaxGroupListTable.vue";
import EditTaxGroupComponent from "./components/EditTaxGroupComponent.vue";
import AddTaxGroupComponent from "./components/AddTaxGroupComponent.vue";
import ViewTaxGroupComponent from "./components/ViewTaxGroupComponent.vue";

export default {
  layout: "DashboardLayout",

  components: {
    TaxGroupListTable,
    NotificationSubscription,
    EditTaxGroupComponent,
    AddTaxGroupComponent,
    ViewTaxGroupComponent,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  data() {
    const taxGroupId = this.$route.query.id;
    const action = this.$route.query.action;
    let isViewTaxGroupModalOpened = false;
    let isEditTaxGroupModalOpened = false;
    let isAddTaxGroupModalOpened = false;
    let openTaxGroup = null;
    if (taxGroupId && action) {
      if (action === QUERY_ACTIONS_VIEW) {
        isViewTaxGroupModalOpened = true;
      } else if (action === QUERY_ACTIONS_EDIT) {
        isEditTaxGroupModalOpened = true;
      }
      openTaxGroup = { id: taxGroupId };
    } else if (action === QUERY_ACTIONS_ADD) {
      isAddTaxGroupModalOpened = true;
    }
    return {
      isViewTaxGroupModalOpened: isViewTaxGroupModalOpened,
      isEditTaxGroupModalOpened: isEditTaxGroupModalOpened,
      isAddTaxGroupModalOpened: isAddTaxGroupModalOpened,
      openTaxGroup: openTaxGroup,
      renderKey: 1,
    };
  },

  methods: {
    openTaxGroupCreateModal() {
      this.closeTaxGroupModal();
      this.isAddTaxGroupModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List TaxGroups",
          query: { action: QUERY_ACTIONS_ADD },
        }).href
      );
    },
    openTaxGroupViewModal(taxGroup, reRender = false) {
      this.closeTaxGroupModal();
      this.openTaxGroup = taxGroup;
      this.isViewTaxGroupModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List TaxGroups",
          query: { id: this.openTaxGroup.id, action: QUERY_ACTIONS_VIEW },
        }).href
      );
    },

    openTaxList() {
      this.$router.push({
        name: "List Taxes",
      });
    },

    openTaxGroupEditModal(taxGroup) {
      this.closeTaxGroupModal();
      this.openTaxGroup = taxGroup;
      this.isEditTaxGroupModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List TaxGroups",
          query: { id: this.openTaxGroup.id, action: QUERY_ACTIONS_EDIT },
        }).href
      );
    },
    closeTaxGroupModal() {
      this.isAddTaxGroupModalOpened = false;
      this.isViewTaxGroupModalOpened = false;
      this.isEditTaxGroupModalOpened = false;
      this.openTaxGroup = null;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List TaxGroups",
          query: {},
        }).href
      );
    },

    async deleteTaxGroup(taxGroup) {
      const confirmation = await swal.fire({
        text: this.$t("TAXES.DELETE_THIS_TAX_GROUP"),
        type: "question",
        customClass: {
          popup: "delete-popup",
        },
        buttonsStyling: false,
        showCancelButton: true,
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonText:
          this.$t("COMMON.YES") + ", " + this.$t("COMMON.DELETE"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch(
            "taxGroups/destroy",
            taxGroup.id
          );
          this.renderKey++;
          this.closeTaxGroupModal();
          this.$notify({
            type: "success",
            message: this.$t("TAXES.TAX_GROUP_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
